import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type CompanyPrefsV1,
  completeOnboarding,
  loadCompanyPrefs,
  saveCompanyPrefs,
  shouldShowOnboarding,
} from "@/data/companyPrefs";
import { WORK_TYPE_LABELS, type RegionId, type WorkType } from "@/types/mvpEstimate";
import { WIZARD_REGION_OPTIONS } from "@/wizard/mvpWizardScopeCatalog";
import { HardHat } from "lucide-react";

const WORK_TYPE_ORDER: WorkType[] = [
  "malowanie",
  "gladzie",
  "tynki",
  "elewacja",
  "wylewki",
  "plytki",
  "podlogi",
  "zabudowy_gk",
  "bialy_montaz",
];

const WORK_TYPE_EMOJI: Record<WorkType, string> = {
  malowanie: "🖌️",
  gladzie: "🪟",
  tynki: "🧱",
  elewacja: "🏠",
  wylewki: "🪣",
  plytki: "🔲",
  podlogi: "🥾",
  zabudowy_gk: "📐",
  bialy_montaz: "🚿",
};

function defaultWorkTypesFromPrefs(prefs: CompanyPrefsV1): WorkType[] {
  if (prefs.preferredWorkTypes && prefs.preferredWorkTypes.length > 0) {
    return prefs.preferredWorkTypes;
  }
  return ["malowanie"];
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoInputId = useId();

  const [form, setForm] = useState<CompanyPrefsV1>(() => loadCompanyPrefs());
  const [workTypes, setWorkTypes] = useState<WorkType[]>(() =>
    defaultWorkTypesFromPrefs(loadCompanyPrefs()),
  );

  useEffect(() => {
    if (!shouldShowOnboarding()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const set = <K extends keyof CompanyPrefsV1>(key: K, val: CompanyPrefsV1[K]) =>
    setForm((p) => ({ ...p, [key]: val }));

  const toggleWorkType = (id: WorkType) => {
    setWorkTypes((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev;
      if (has) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const onLogoFile = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : undefined;
      set("logoDataUrl", dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    set("logoDataUrl", undefined);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleSave = () => {
    saveCompanyPrefs({
      ...form,
      companyName: form.companyName.trim(),
      preferredWorkTypes: workTypes,
    });
    completeOnboarding(false);
    navigate("/", { replace: true });
  };

  const handleSkip = () => {
    completeOnboarding(true);
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10 md:py-14">
      <div className="mx-auto max-w-xl">
        <div className="mb-10 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary shadow-sm">
            <HardHat className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Powitanie</h1>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              Kilka informacji — później dopasujesz wszystko w ustawieniach.
            </p>
          </div>
        </div>

        <div className="space-y-12">
          <section className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">Jak się nazywa Twoja firma?</h2>
            <div className="space-y-2">
              <Label htmlFor="onb-name" className="sr-only">
                Nazwa firmy lub imię i nazwisko
              </Label>
              <Input
                id="onb-name"
                value={form.companyName}
                onChange={(e) => set("companyName", e.target.value)}
                placeholder="np. Jan Kowalski Remonty"
                className="h-12 text-base"
                autoComplete="organization"
              />
              <p className="text-xs text-muted-foreground">
                Pojawi się na ofertach wysyłanych do klientów
              </p>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">Logo (opcjonalnie)</span>
              <input
                ref={logoInputRef}
                id={logoInputId}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => onLogoFile(e.target.files)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                  Dodaj logo
                </Button>
                {form.logoDataUrl ? (
                  <>
                    <div className="h-12 w-12 overflow-hidden rounded-lg border border-border bg-muted">
                      <img src={form.logoDataUrl} alt="" className="h-full w-full object-contain" />
                    </div>
                    <button
                      type="button"
                      className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
                      onClick={clearLogo}
                    >
                      Usuń
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">Jakie prace wykonujesz?</h2>
            <p className="text-xs text-muted-foreground">Możesz zaznaczyć kilka — jak w kreatorze kosztorysu.</p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {WORK_TYPE_ORDER.map((id) => {
                const checked = workTypes.includes(id);
                return (
                  <li key={id}>
                    <label
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-sm transition-colors ${
                        checked
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border bg-card text-foreground hover:border-border/80"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleWorkType(id)}
                        className="h-4 w-4 shrink-0 rounded border-2 border-primary accent-primary"
                      />
                      <span className="text-lg leading-none" aria-hidden>
                        {WORK_TYPE_EMOJI[id]}
                      </span>
                      <span className="font-medium leading-snug">{WORK_TYPE_LABELS[id]}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">W jakiej strefie cenowej pracujesz?</h2>
            <p className="text-xs text-muted-foreground">Wpływa na domyślne stawki w kosztorysie</p>
            <div className="grid gap-2">
              {WIZARD_REGION_OPTIONS.map((o) => {
                const active = form.region === o.id;
                return (
                  <label
                    key={o.id}
                    className={`flex cursor-pointer flex-col rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                      active
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border bg-card hover:border-border/60"
                    }`}
                  >
                    <span className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="onb-region"
                        checked={active}
                        onChange={() => set("region", o.id as RegionId)}
                        className="mt-0.5 h-4 w-4 shrink-0 border-primary accent-primary"
                      />
                      <span>
                        <span className="block font-medium">{o.label}</span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">{o.hint}</span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          <div className="flex flex-col gap-3 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleSkip}
              className="order-2 text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline sm:order-1 sm:text-left"
            >
              Pomiń na razie
            </button>
            <Button
              type="button"
              variant="action"
              size="lg"
              onClick={handleSave}
              className="order-1 w-full sm:order-2 sm:w-auto"
            >
              Zacznij →
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            NIP, adres czy dane do faktury — dodasz później w{" "}
            <Link to="/ustawienia" className="font-medium text-foreground underline underline-offset-2">
              Ustawieniach
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
